import React, { useState, useEffect, useRef } from 'react';
import { B } from '../constants';
import { flights } from '../api';
import Input from './ui/Input';

// Debounced airport search. The backend calls Amadeus's reference-data
// locations endpoint; we cache the most recent results per keyword so flipping
// focus between inputs doesn't refetch.
export default function AirportAutocomplete({
  label,
  value,
  onChange,
  placeholder = 'City or airport',
  required = false,
  style,
}) {
  const [query, setQuery] = useState(value?.label || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value?.label && value.label !== query) {
      setQuery(value.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.iataCode]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    // Skip autocomplete if user typed an exact IATA they already selected
    if (value?.label === query) return;

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const { airports } = await flights.airports(query);
        setResults(airports || []);
        setOpen(true);
      } catch (err) {
        setError(err.message || 'Airport lookup failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSelect = (airport) => {
    const label = `${airport.cityName || airport.name} (${airport.iataCode})`;
    onChange({
      iataCode: airport.iataCode,
      label,
      cityName: airport.cityName,
      countryName: airport.countryName,
    });
    setQuery(label);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {label && (
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>
          {label}
        </label>
      )}
      <Input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value?.iataCode) onChange(null);
        }}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        required={required}
        style={style}
        autoComplete="off"
        icon="🔍"
      />
      {loading && (
        <div style={{ position: 'absolute', right: 12, top: label ? 44 : 21, fontSize: 12, color: B.muted }}>
          …
        </div>
      )}
      {error && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#dc3545' }}>{error}</div>
      )}
      {open && results.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            margin: '4px 0 0',
            padding: 4,
            listStyle: 'none',
            maxHeight: 260,
            overflowY: 'auto',
            zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          {results.map((a) => (
            <li
              key={a.iataCode + a.name}
              role="option"
              aria-selected={value?.iataCode === a.iataCode}
              onClick={() => handleSelect(a)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderRadius: 8,
                fontSize: 14,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span>
                <strong>{a.cityName || a.name}</strong>
                {a.countryName && (
                  <span style={{ color: B.muted, marginLeft: 6, fontSize: 12 }}>{a.countryName}</span>
                )}
                <div style={{ color: B.muted, fontSize: 12 }}>{a.name}</div>
              </span>
              <span
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: B.forest,
                  alignSelf: 'center',
                }}
              >
                {a.iataCode}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
